import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { CoGuardian } from '../types';

type Props = { guardians: CoGuardian[]; tablet?: boolean };

export function CoGuardiansSection({ guardians, tablet = false }: Props) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack gap={scale(12)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitle}>
        {tablet
          ? STRINGS.guardianProfile.coGuardians.tabletTitle
          : STRINGS.guardianProfile.coGuardians.phoneTitle}
      </Text>
      <Stack gap={scale({ phone: 12, tablet: 0 })}>
        {guardians.map((guardian, index) => (
          <React.Fragment key={guardian.id}>
            <Pressable
              style={({ pressed }) => [
                styles.card,
                tablet ? styles.tabletCard : null,
                pressed && styles.pressed,
              ]}>
              <Image source={guardian.avatar} width={scale(60)} height={scale(60)} circular />
              <Stack flex={1}>
                <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.name}>
                  {guardian.name}
                </Text>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                  style={styles.summary}>
                  {guardian.relationshipSummary}
                </Text>
              </Stack>
              <SvgIcon name="IcChevronRight" size={scale(20)} color={guardianColors['6']} />
            </Pressable>
            {tablet && index < guardians.length - 1 ? <Stack style={styles.divider} /> : null}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  sectionTitle: { color: guardianColors['10'] },
  card: {
    height: scale(76),
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(10),
  },
  tabletCard: { paddingHorizontal: scale(16), paddingVertical: scale(8) },
  divider: { height: 0, borderTopWidth: 1, borderTopColor: guardianColors['3'] },
  name: { color: guardianColors['10'] },
  summary: { color: guardianColors['10'] },
  pressed: { opacity: 0.68 },
}));
